<template>
    <div class="permissions-page">
        <div class="page-route-select">
            <router-link to="/dashboard" class="page-route-select-text">
                Gerencial
            </router-link>
            <span class="material-icons page-route-select-icon">play_arrow</span>
            <div class="page-route-select-text">Permissoes</div>
        </div>

        <div class="permissions-page__header">
            <div>
                <h1 class="permissions-page__title">Gestao de Permissoes</h1>
                <p class="permissions-page__subtitle">
                    Cadastre, edite e remova permissoes em uma unica tela.
                </p>
            </div>
            <button class="permissions-page__new-button" :disabled="isAdding || editingExists || saving" @click="startAdd">
                <span class="material-icons-outlined">add</span>
                Nova permissao
            </button>
        </div>

        <div class="permissions-page__filters">
            <label class="permissions-page__field permissions-page__field--wide">
                <span>Buscar</span>
                <input v-model.trim="filters.query" type="text" placeholder="Nome, recurso, acao ou descricao" />
            </label>
            <button class="permissions-page__ghost-button" @click="clearFilters">
                Limpar filtros
            </button>
        </div>

        <div v-if="successMessage" class="permissions-page__feedback permissions-page__feedback--success">
            {{ successMessage }}
        </div>
        <div v-if="error" class="permissions-page__feedback permissions-page__feedback--error">
            {{ error }}
        </div>

        <div class="permissions-page__table-wrapper">
            <div v-if="loading" class="permissions-page__table-loader">
                <div class="cc-loader">
                    <img src="/images/loader.svg" />
                </div>
            </div>
            <table class="permissions-page__table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Recurso</th>
                        <th>Acao</th>
                        <th>Nome</th>
                        <th>Descricao</th>
                        <th>Visibilidade</th>
                        <th>Perfis</th>
                        <th>Acoes</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="isAdding" class="permissions-page__row permissions-page__row--new">
                        <td>Novo</td>
                        <td>
                            <input v-model.trim="newPermission.resource" type="text" placeholder="RESOURCE" />
                        </td>
                        <td>
                            <input v-model.trim="newPermission.action" type="text" placeholder="READ" />
                        </td>
                        <td>
                            <input v-model.trim="newPermission.name" type="text" placeholder="permission.read" />
                        </td>
                        <td>
                            <textarea v-model.trim="newPermission.description" rows="2" placeholder="Descricao da permissao"></textarea>
                        </td>
                        <td>
                            <div class="permissions-page__visibility-group">
                                <button
                                    v-for="option in visibilityOptions"
                                    :key="option.value"
                                    type="button"
                                    class="permissions-page__visibility-button"
                                    :class="{ 'permissions-page__visibility-button--active': newPermission.visibility === option.value }"
                                    @click="setVisibility(newPermission, option.value)"
                                >
                                    {{ option.label }}
                                </button>
                            </div>
                        </td>
                        <td>-</td>
                        <td>
                            <div class="permissions-page__actions">
                                <button
                                    class="permissions-page__icon-button permissions-page__icon-button--save"
                                    :disabled="saving"
                                    @click="saveNewPermission"
                                >
                                    <span class="material-icons-outlined">save</span>
                                </button>
                                <button class="permissions-page__icon-button" :disabled="saving" @click="cancelAdd">
                                    <span class="material-icons-outlined">close</span>
                                </button>
                            </div>
                        </td>
                    </tr>

                    <template v-for="group in groupedPermissions">
                    <tr :key="`${group.resource}-group`" class="permissions-page__group-row" @click="toggleGroup(group.resource)">
                        <td colspan="8" class="permissions-page__group-cell">
                            <div class="permissions-page__group-content">
                                <div class="permissions-page__group-title">
                                    <span class="material-icons permissions-page__group-icon">
                                        {{ isGroupCollapsed(group.resource) ? "chevron_right" : "expand_more" }}
                                    </span>
                                    <span>{{ translateResource(group.resource) }}</span>
                                </div>
                                <span class="permissions-page__group-count">{{ group.permissions.length }}</span>
                            </div>
                        </td>
                    </tr>

                    <tr
                        v-for="permission in group.permissions"
                        v-show="!isGroupCollapsed(group.resource)"
                        :key="permission.id"
                        class="permissions-page__row"
                    >
                        <td>#{{ permission.id }}</td>
                        <td>
                            <span v-if="!permission.editing">{{ translateResource(permission.resource) }}</span>
                            <input v-else v-model.trim="permission.form.resource" type="text" placeholder="RESOURCE" />
                        </td>
                        <td>
                            <span v-if="!permission.editing">{{ translateAction(permission.action) }}</span>
                            <input v-else v-model.trim="permission.form.action" type="text" placeholder="READ" />
                        </td>
                        <td>
                            <span v-if="!permission.editing">{{ permission.name }}</span>
                            <input v-else v-model.trim="permission.form.name" type="text" placeholder="Nome" />
                        </td>
                        <td class="permissions-page__description-cell">
                            <span v-if="!permission.editing">{{ permission.description }}</span>
                            <textarea v-else v-model.trim="permission.form.description" rows="2" placeholder="Descricao"></textarea>
                        </td>
                        <td>
                            <div class="permissions-page__visibility-group">
                                <button
                                    v-for="option in visibilityOptions"
                                    :key="`${permission.id}-${option.value}`"
                                    type="button"
                                    class="permissions-page__visibility-button"
                                    :class="{ 'permissions-page__visibility-button--active': permission.visibility === option.value }"
                                    :disabled="saving || isAdding"
                                    @click="updateVisibility(permission, option.value)"
                                >
                                    {{ option.label }}
                                </button>
                            </div>
                        </td>
                        <td>{{ permission.profiles_count != null ? permission.profiles_count : 0 }}</td>
                        <td>
                            <div class="permissions-page__actions">
                                <button
                                    v-if="!permission.editing"
                                    class="permissions-page__icon-button"
                                    :disabled="isAdding || saving"
                                    @click="startEdit(permission)"
                                >
                                    <span class="material-icons-outlined">edit</span>
                                </button>
                                <button
                                    v-else
                                    class="permissions-page__icon-button permissions-page__icon-button--save"
                                    :disabled="saving"
                                    @click="saveEdit(permission)"
                                >
                                    <span class="material-icons-outlined">save</span>
                                </button>
                                <button
                                    v-if="permission.editing"
                                    class="permissions-page__icon-button"
                                    :disabled="saving"
                                    @click="cancelEdit(permission)"
                                >
                                    <span class="material-icons-outlined">close</span>
                                </button>
                                <button
                                    v-else
                                    class="permissions-page__icon-button permissions-page__icon-button--danger"
                                    :disabled="isAdding || saving"
                                    @click="removePermission(permission)"
                                >
                                    <span class="material-icons-outlined">delete</span>
                                </button>
                            </div>
                        </td>
                    </tr>
                    </template>

                    <tr v-if="!loading && groupedPermissions.length === 0 && !isAdding">
                        <td colspan="8" class="permissions-page__empty-state">
                            Nenhuma permissao encontrada para os filtros informados.
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="loading" class="overlay">
            <div class="cc-loader">
                <img src="/images/loader.svg" />
            </div>
        </div>
    </div>
</template>

<script>
import PermissionService from "@/services/v3/management/permission.service";
import {
    VISIBILITY_OPTIONS,
    extractCollectionFromPayload,
    extractItemFromPayload,
    normalizePermissionRecord,
    translatePermissionAction,
    translatePermissionResource,
} from "@/helpers/permissions";

const createEmptyPermission = () => ({
    resource: "",
    action: "",
    name: "",
    description: "",
    visibility: VISIBILITY_OPTIONS[0].value,
});

export default {
    name: "PermissionsPage",
    data() {
        return {
            permissionService: new PermissionService(),
            permissions: [],
            visibilityOptions: VISIBILITY_OPTIONS,
            filters: {
                query: "",
            },
            collapsedGroups: {},
            isAdding: false,
            newPermission: createEmptyPermission(),
            loading: false,
            saving: false,
            error: null,
            successMessage: null,
        };
    },
    computed: {
        editingExists() {
            return this.permissions.some((permission) => permission.editing);
        },
        filteredPermissions() {
            const query = this.filters.query.toLowerCase();

            return this.permissions.filter((permission) => {
                const matchesQuery =
                    !query ||
                    [permission.name, permission.resource, permission.action, permission.description].some((value) =>
                        (value || "").toLowerCase().includes(query)
                    );

                return matchesQuery;
            });
        },
        groupedPermissions() {
            const groups = this.filteredPermissions.reduce((carry, permission) => {
                const existing = carry.find((group) => group.resource === permission.resource);

                if (existing) {
                    existing.permissions.push(permission);
                    return carry;
                }

                carry.push({
                    resource: permission.resource,
                    permissions: [permission],
                });

                return carry;
            }, []);

            return groups.sort((left, right) =>
                this.translateResource(left.resource).localeCompare(this.translateResource(right.resource))
            );
        },
    },
    methods: {
        normalizePermission(permission) {
            const normalizedPermission = normalizePermissionRecord(permission);

            return {
                ...normalizedPermission,
                profiles_count:
                    normalizedPermission.profiles_count != null
                        ? normalizedPermission.profiles_count
                        : normalizedPermission.profiles
                        ? normalizedPermission.profiles.length
                        : 0,
                editing: false,
                form: {
                    resource: normalizedPermission.resource,
                    action: normalizedPermission.action,
                    name: normalizedPermission.name,
                    description: normalizedPermission.description,
                    visibility: normalizedPermission.visibility,
                },
            };
        },
        async loadPermissions() {
            this.loading = true;
            this.error = null;

            try {
                const response = await this.permissionService.list();
                const permissions = extractCollectionFromPayload(response.data);

                this.permissions = permissions.map(this.normalizePermission);
            } catch (error) {
                this.error = "Erro ao carregar as permissoes.";
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        startAdd() {
            this.clearMessages();
            this.permissions.forEach((permission) => this.cancelEdit(permission));
            this.isAdding = true;
            this.newPermission = createEmptyPermission();
        },
        cancelAdd() {
            this.isAdding = false;
            this.newPermission = createEmptyPermission();
        },
        startEdit(permission) {
            this.clearMessages();
            this.isAdding = false;
            this.permissions.forEach((item) => {
                if (item.id !== permission.id) {
                    this.cancelEdit(item);
                }
            });
            permission.editing = true;
            permission.form = {
                resource: permission.resource,
                action: permission.action,
                name: permission.name,
                description: permission.description,
                visibility: permission.visibility || "all",
            };
        },
        cancelEdit(permission) {
            permission.editing = false;
            permission.form = {
                resource: permission.resource,
                action: permission.action,
                name: permission.name,
                description: permission.description,
                visibility: permission.visibility || "all",
            };
        },
        setVisibility(target, visibility) {
            target.visibility = visibility;
        },
        toggleGroup(resource) {
            this.$set(this.collapsedGroups, resource, !this.collapsedGroups[resource]);
        },
        isGroupCollapsed(resource) {
            return !!this.collapsedGroups[resource];
        },
        translateResource(resource) {
            return translatePermissionResource(resource);
        },
        translateAction(action) {
            return translatePermissionAction(action);
        },
        validatePermission(payload) {
            if (!payload.resource || !payload.action || !payload.name || !payload.description || !payload.visibility) {
                this.error = "Preencha recurso, acao, nome, descricao e visibilidade.";
                return false;
            }

            return true;
        },
        async saveNewPermission() {
            const payload = { ...this.newPermission };

            if (!this.validatePermission(payload)) {
                return;
            }

            this.saving = true;
            this.clearMessages();

            try {
                await this.permissionService.create(payload);
                this.cancelAdd();
                await this.loadPermissions();
                this.successMessage = "Permissao criada com sucesso.";
            } catch (error) {
                this.error = this.extractError(error, "Erro ao criar a permissao.");
            } finally {
                this.saving = false;
            }
        },
        async saveEdit(permission) {
            const payload = { ...permission.form };

            if (!this.validatePermission(payload)) {
                return;
            }

            this.saving = true;
            this.clearMessages();

            try {
                const response = await this.permissionService.update(permission.id, payload);
                const updatedPermission = this.normalizePermission(extractItemFromPayload(response.data));
                const permissionIndex = this.permissions.findIndex((item) => item.id === permission.id);

                if (permissionIndex !== -1) {
                    this.$set(this.permissions, permissionIndex, updatedPermission);
                }

                this.successMessage = "Permissao atualizada com sucesso.";
            } catch (error) {
                this.error = this.extractError(error, "Erro ao atualizar a permissao.");
            } finally {
                this.saving = false;
            }
        },
        async updateVisibility(permission, visibility) {
            if (permission.visibility === visibility) {
                return;
            }

            const previousVisibility = permission.visibility;

            this.saving = true;
            this.clearMessages();
            permission.visibility = visibility;

            try {
                const response = await this.permissionService.update(permission.id, { visibility });
                const updatedPermission = this.normalizePermission(extractItemFromPayload(response.data));
                const permissionIndex = this.permissions.findIndex((item) => item.id === permission.id);

                if (permissionIndex !== -1) {
                    this.$set(this.permissions, permissionIndex, updatedPermission);
                }

                this.successMessage = "Visibilidade atualizada com sucesso.";
            } catch (error) {
                permission.visibility = previousVisibility;
                this.error = this.extractError(error, "Erro ao atualizar a visibilidade.");
            } finally {
                this.saving = false;
            }
        },
        async removePermission(permission) {
            if (!window.confirm(`Deseja excluir a permissao "${permission.name}"?`)) {
                return;
            }

            this.saving = true;
            this.clearMessages();

            try {
                await this.permissionService.delete(permission.id);
                this.permissions = this.permissions.filter((item) => item.id !== permission.id);
                this.successMessage = "Permissao removida com sucesso.";
            } catch (error) {
                this.error = this.extractError(error, "Erro ao remover a permissao.");
            } finally {
                this.saving = false;
            }
        },
        clearFilters() {
            this.filters = {
                query: "",
            };
        },
        clearMessages() {
            this.error = null;
            this.successMessage = null;
        },
        extractError(error, fallbackMessage) {
            const apiErrors = error && error.response && error.response.data && error.response.data.errors;

            if (Array.isArray(apiErrors) && apiErrors.length > 0) {
                return apiErrors.join(" ");
            }

            if (apiErrors && typeof apiErrors === "object") {
                return Object.values(apiErrors)
                    .reduce((carry, value) => carry.concat(value), [])
                    .join(" ");
            }

            return fallbackMessage;
        },
    },
    mounted() {
        this.loadPermissions();
    },
};
</script>

<style scoped lang="scss">
.permissions-page {
    padding: 0.75rem 2rem 2rem 0rem;
}

.permissions-page__header {
    margin: 1.5rem 0;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
}

.permissions-page__title {
    margin: 0;
    font-size: 2rem;
    color: #303030;
}

.permissions-page__subtitle {
    margin: 0.4rem 0 0;
    color: #6e6e6e;
}

.permissions-page__new-button,
.permissions-page__ghost-button,
.permissions-page__icon-button {
    border: none;
    border-radius: 10px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-weight: 600;
}

.permissions-page__new-button {
    background: #ff7110;
    color: #fff;
    padding: 0.9rem 1.2rem;
}

.permissions-page__ghost-button {
    background: #fff3ea;
    color: #c95d10;
    padding: 0.9rem 1rem;
}

.permissions-page__filters {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 1rem;
    margin-bottom: 1rem;
    align-items: end;
}

.permissions-page__field {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    color: #4b4b4b;
    font-size: 0.95rem;
}

.permissions-page__field--wide {
    min-width: 0;
}

.permissions-page__field input,
.permissions-page__field select,
.permissions-page__table input,
.permissions-page__table select,
.permissions-page__table textarea {
    width: 100%;
    border: 1px solid #d7d7d7;
    border-radius: 8px;
    padding: 0.7rem 0.8rem;
    background: #fff;
    font: inherit;
    color: #303030;
}

.permissions-page__table textarea {
    resize: vertical;
    min-height: 70px;
}

.permissions-page__feedback {
    margin-bottom: 1rem;
    padding: 0.9rem 1rem;
    border-radius: 10px;
    font-weight: 600;
}

.permissions-page__feedback--success {
    background: #edf9f0;
    color: #207245;
}

.permissions-page__feedback--error {
    background: #ffefef;
    color: #b02a37;
}

.permissions-page__group-row {
    background: #faf7f4;
    cursor: pointer;
}

.permissions-page__group-cell {
    padding: 0.85rem 1rem;
}

.permissions-page__group-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.permissions-page__group-title {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-weight: 700;
    color: #4b3424;
}

.permissions-page__group-icon {
    font-size: 1.15rem;
}

.permissions-page__group-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    height: 2rem;
    padding: 0 0.6rem;
    border-radius: 999px;
    background: #fff;
    color: #c95d10;
    font-weight: 700;
}

.permissions-page__visibility-group {
    display: flex;
    gap: 0.45rem;
    flex-wrap: nowrap;
    white-space: nowrap;
}

.permissions-page__visibility-button {
    border: 1px solid #d7d7d7;
    background: #fff;
    color: #4b4b4b;
    border-radius: 999px;
    padding: 0.4rem 0.7rem;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
    transition: 0.15s ease;
}

.permissions-page__visibility-button--active {
    background: #ff7110;
    border-color: #ff7110;
    color: #fff;
}


.permissions-page__table-wrapper {
    position: relative;
    background: #fff;
    border: 1px solid #ececec;
    border-radius: 18px;
    overflow: auto;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.04);
}

.permissions-page__table-loader {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.75);
    z-index: 1;
}

.permissions-page__table {
    width: 100%;
    min-width: 1180px;
    border-collapse: collapse;
}

.permissions-page__table th,
.permissions-page__table td {
    padding: 1rem;
    border-bottom: 1px solid #f1f1f1;
    vertical-align: top;
    text-align: left;
}

.permissions-page__table th {
    color: #686868;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: #fffaf6;
}

.permissions-page__row--new {
    background: #fff9f4;
}

.permissions-page__description-cell {
    min-width: 300px;
}

.permissions-page__actions {
    display: flex;
    gap: 0.5rem;
}

.permissions-page__icon-button {
    width: 40px;
    height: 40px;
    background: #f5f5f5;
    color: #4b4b4b;
}

.permissions-page__icon-button--save {
    background: #ebf6ff;
    color: #1267b3;
}

.permissions-page__icon-button--danger {
    background: #ffefef;
    color: #b02a37;
}

.permissions-page__empty-state {
    text-align: center;
    color: #6e6e6e;
}

.page-route-select {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    color: #7b7b7b;
    font-size: 0.95rem;
}

.page-route-select-text {
    color: inherit;
    text-decoration: none;
}

.page-route-select-icon {
    font-size: 1rem;
}

@media only screen and (max-width: 1080px) {
    .permissions-page {
        padding: 0.75rem 1rem 1.5rem 0;
    }

    .permissions-page__header {
        flex-direction: column;
    }

    .permissions-page__filters {
        grid-template-columns: 1fr;
    }
}
</style>
